import { css, html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type {
    Commerce,
    CommerceShareAcceptanceStrategy,
    CommerceSharePreview,
} from "@teqtank/commerce-sdk";

@customElement("teqtank-share-receiver")
export class TeqtankShareReceiver extends LitElement {
    @property({ attribute: false }) public accessor commerce: Commerce | undefined;
    @property({ attribute: "share-id" }) public accessor shareId = "";
    @property() public accessor heading = "A recommendation was shared with you";
    @state() private accessor preview: CommerceSharePreview | undefined;
    @state() private accessor busy = false;
    @state() private accessor status = "";
    public static override styles = css`
        :host {
            container-type: inline-size;
            display: block;
            font-family: Inter, ui-sans-serif, system-ui, sans-serif;
        }
        .receiver {
            display: grid;
            gap: 1rem;
            padding: 1.25rem;
            border: 1px solid var(--teqtank-color-border, #ddd7cc);
            border-radius: 1rem;
            background: var(--teqtank-color-surface, #fff);
        }
        h2,
        p {
            margin: 0;
        }
        .muted {
            color: var(--teqtank-color-text-muted, #68645d);
        }
        .items {
            display: grid;
            gap: 0.65rem;
        }
        .item {
            display: grid;
            grid-template-columns: 1fr auto;
            gap: 1rem;
            padding: 0.75rem 0;
            border-top: 1px solid var(--teqtank-color-border, #eee);
        }
        .contact {
            padding: 0.8rem;
            border-radius: 0.8rem;
            background: var(--teqtank-color-surface-soft, #f7f5f1);
        }
        .actions {
            display: flex;
            flex-wrap: wrap;
            gap: 0.6rem;
        }
        button {
            padding: 0.7rem 1rem;
            border: 1px solid var(--teqtank-color-border, #ccc);
            border-radius: 999px;
            background: transparent;
            font: inherit;
            font-weight: 700;
            cursor: pointer;
        }
        .primary {
            color: #fff;
            background: var(--teqtank-color-accent, #01676b);
            border-color: var(--teqtank-color-accent, #01676b);
        }
    `;
    protected override updated(changed: Map<PropertyKey, unknown>): void {
        if ((changed.has("shareId") || changed.has("commerce")) && this.shareId && this.commerce)
            void this.load();
    }
    protected override render() {
        if (!this.shareId) return html``;
        if (!this.preview)
            return html`<section class="receiver">
                <p>${this.status || "Loading shared experience…"}</p>
            </section>`;
        return html`<section class="receiver">
            <div>
                <p class="muted">SHARED COMMERCE</p>
                <h2>${this.heading}</h2>
                <p>${this.preview.resource.description}</p>
            </div>
            ${
                this.preview.contact
                    ? html`<div class="contact">
                          <strong>${this.preview.contact.displayName}</strong>
                          <p class="muted">${this.preview.contact.role}</p>
                      </div>`
                    : null
            }
            <div class="items">
                ${this.preview.items.map(
                    (item) =>
                        html`<div class="item">
                            <div>
                                <strong>${item.name}</strong>
                                <p class="muted">
                                    ${
                                        item.purchaseType === "recurring"
                                            ? "Subscribe & save"
                                            : "One-time purchase"
                                    }
                                    · Qty ${item.quantity}
                                </p>
                            </div>
                            <strong>${item.unitPrice.formatted}</strong>
                        </div>`,
                )}
            </div>
            ${
                this.preview.subtotal
                    ? html`<p>
                          <strong
                              >${this.preview.itemCount} items ·
                              ${this.preview.subtotal.formatted}</strong
                          >
                      </p>`
                    : null
            }
            <div class="actions">
                ${
                    this.preview.acceptanceStrategies.includes("merge")
                        ? html`<button
                              class="primary"
                              type="button"
                              ?disabled=${this.busy || !this.preview.canAccept}
                              @click=${() => this.accept("merge")}
                          >
                              Add to my bag
                          </button>`
                        : null
                }
                ${
                    this.preview.acceptanceStrategies.includes("replace")
                        ? html`<button
                              class=${
                                  this.preview.acceptanceStrategies.includes("merge")
                                      ? ""
                                      : "primary"
                              }
                              type="button"
                              ?disabled=${this.busy || !this.preview.canAccept}
                              @click=${() => this.accept("replace")}
                          >
                              ${
                                  this.preview.experience === "enrollment"
                                      ? "Continue this enrollment"
                                      : "Replace my bag"
                              }
                          </button>`
                        : null
                }
            </div>
            <p class="muted" aria-live="polite">${this.status}</p>
        </section>`;
    }
    private async load(): Promise<void> {
        this.status = "Loading shared experience…";
        try {
            this.preview = await this.commerce!.share.resolve(this.shareId);
            this.status = "";
        } catch {
            this.status = "This shared experience is unavailable or has expired.";
        }
    }
    private async accept(strategy: CommerceShareAcceptanceStrategy): Promise<void> {
        this.busy = true;
        this.status = strategy === "merge" ? "Adding recommendations…" : "Starting a new bag…";
        try {
            const cart = await this.commerce!.share.accept(this.shareId, strategy);
            this.status = `${cart.totals.itemCount} items are now in your bag.`;
            this.dispatchEvent(
                new CustomEvent("teqtank-share-accepted", {
                    detail: { preview: this.preview, cart, strategy },
                    bubbles: true,
                    composed: true,
                }),
            );
        } finally {
            this.busy = false;
        }
    }
}
declare global {
    interface HTMLElementTagNameMap {
        "teqtank-share-receiver": TeqtankShareReceiver;
    }
}
